import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { viewEvent, eventSummary } from "services/api/events";
import { isHotelProduct, isTicketProduct } from "../utils/dashboardUtils";

const useEventDashboard = () => {
  const { event_code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const eventIdFromState = location.state?.id;

  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const scrollContainer = document.querySelector(".matx-content-container");
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    }
  }, [event_code]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let eventId = eventIdFromState;
        if (!eventId) {
          console.error("No event ID provided");
          setLoading(false);
          return;
        }

        const [eventRes, summaryRes] = await Promise.all([
          viewEvent(eventId),
          eventSummary(eventId)
        ]);

        if (eventRes?.status === 200 || eventRes?.status === 201) {
          setEventData(eventRes.data.data.event);
        }

        if (summaryRes?.status === "success" || summaryRes?.status === 200) {
          setSummaryData(summaryRes.data.event_summary);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eventIdFromState]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleReviewPayments = useCallback(() => {
    navigate(`/events/review-payments/${event_code}`, {
      state: { id: eventData?.id },
    });
  }, [navigate, event_code, eventData?.id]);

  const handleNavigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const products = useMemo(() => {
    const sorted = [...(summaryData?.products || [])].sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
    );

    return {
      tickets: sorted.filter(isTicketProduct),
      hotels: sorted.filter(isHotelProduct),
      addons: sorted.filter((p) => !isHotelProduct(p) && !isTicketProduct(p)),
    };
  }, [summaryData?.products]);

  return {
    loading,
    eventData,
    summaryData,
    tabValue,
    products,
    event_code,
    handleTabChange,
    handleReviewPayments,
    handleNavigateBack,
  };
};

export default useEventDashboard;
